import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Phase 19b — FiguWallet client hooks.
 *
 * Balance is sourced from AuthContext.user.figutokens_balance (which
 * comes from /api/auth/me). After any mutation that changes the
 * balance, callers should invoke `useAuth().refetch()`.
 */

// GET /api/payments/mode — probe the current shop mode + beta flag.
export function usePaymentsMode() {
  return useQuery({
    queryKey: ["payments", "mode"],
    queryFn: async () => (await api.get("/payments/mode")).data,
    // Prevent stale gating: refetch on modal reopen.
    staleTime: 30_000,
  });
}

// === LOCAL SANDBOX — remove before production deploy ===
// GET /api/payments/config — sandbox health probe. Tells the UI whether
// the mock-checkout path is active (i.e. server is running with
// `sk_test_mock_pod_key`). Secret values are NOT included in the
// response; only prefixes and presence booleans.
export function usePaymentsConfig() {
  return useQuery({
    queryKey: ["payments", "config"],
    queryFn: async () => (await api.get("/payments/config")).data,
    staleTime: 30_000,
  });
}

// === LOCAL SANDBOX — POST /api/payments/mock-checkout ===
// Only functional when the backend is running with `sk_test_mock_*`.
// Returns `{status: "credited", credit, new_balance}` for token bundles
// and `{status: "subscription_activated", tier, ...}` for subs.
export function useMockCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ sku }) =>
      (await api.post("/payments/mock-checkout", { sku })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

// POST /api/payments/create-checkout-session — Stripe hosted checkout.
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async ({ sku }) =>
      (await api.post("/payments/create-checkout-session", { sku })).data,
  });
}

// POST /api/boosters/purchase — spend FiguTokens on a booster.
export function usePurchaseBooster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ booster }) =>
      (await api.post("/boosters/purchase", { booster })).data,
    onSuccess: () => {
      // Invalidate any query that includes the user (auth/me is the
      // canonical source of `figutokens_balance` + `active_booster`).
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
      qc.invalidateQueries({ queryKey: ["radar", "matches"] });
    },
  });
}

// Product catalog — kept client-side because it's UI copy only.
// Server enforces canonical prices in the webhook + booster router.
export const TOKEN_BUNDLES = [
  { sku: "tokens_500", price_display: "$5", tokens: 50 },
  { sku: "tokens_1000", price_display: "$10", tokens: 110 },
  { sku: "tokens_1500", price_display: "$15", tokens: 180 },
];

export const BOOSTERS = [
  { key: "proximity_lightning", cost: 40, duration_days: 1 },
  { key: "streak_minibus", cost: 50, duration_days: 3 },
  { key: "quick_boost_3d", cost: 60, duration_days: 3 },
];

export const SUBSCRIPTIONS = [
  { sku: "sub_pro", price_display: "$4.99/mo" },
  { sku: "sub_master", price_display: "$9.99/mo" },
];
